import {
  Button,
  DatePicker,
  Image,
  InputNumber,
  Popover,
  Space,
  TableProps,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import DoubleCheckIcon from "../../../assets/icons/DoubleCheckIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import InfoIcon from "../../../assets/icons/InfoIcon";
import PdfIcon from "../../../assets/icons/PdfIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import PrintableContent from "../../../components/common/PrintableContent";
import TopCard from "../../../components/common/TopCard";
import ExpanseUserInfoSkeleton from "../../../components/Expanse/ExpanseUserInfoSkeleton";
import VisitDetailsModal from "../../../components/Expanse/VisitDetailsModal";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomModal from "../../../components/ui/CustomModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useExpenseAcceptMutation,
  useExpenseMonthlyDetailsQuery,
  useExpenseRejectMutation,
  useExpenseUpdateMutation,
  useLazyDownloadEmployeeExpenseQuery,
} from "../../../features/expense/expenseApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import usePrint from "../../../hooks/usePrint";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { GetExpenseDetailsResponseType } from "../../../types";
import { addCommaInNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, backLink } from "../../../utils/utils";
import NotFound from "../../NotFound";
import ExpenseDetailsPDF from "./ExpenseDetailsPDF";

interface EditAmountType {
  id: number;
  amount?: number;
}

type TableType = GetExpenseDetailsResponseType["data"]["expenses"][0];

type ModalType =
  | "REJECT"
  | "APPROVE"
  | "VIEW_ATTACHMENT"
  | "VIEW_VISIT_DETAILS";

export default function ExpenseDetails() {
  const { viewId } = useParams();
  const location = useLocation();
  const { open, handleOpen, handleClose } = useModal<number, ModalType>();
  const [month, setMonth] = useState<Dayjs | null>(dayjs());
  const [editAmount, setEditAmount] = useState<EditAmountType | undefined>(
    undefined
  );
  const inputRef = useRef<any>(null);
  const [search] = useSearchParams();

  const [
    rejectExpense,
    {
      isError: isRejectError,
      error: rejectError,
      isSuccess: isRejectSuccess,
      isLoading: isRejectLoading,
    },
  ] = useExpenseRejectMutation();
  const [
    expenseUpdate,
    {
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
      isLoading: isUpdateLoading,
    },
  ] = useExpenseUpdateMutation();
  const { isLoading, isError, error, data } = useExpenseMonthlyDetailsQuery(
    {
      params: {
        business_member_id: Number(viewId),
        ...(month ? { month: dayjs(month).format("MM-YYYY") } : {}),
      },
    },
    {
      skip: !viewId,
    }
  );

  const [downloadEmployeeExpense, { isLoading: isDownloading, data: pdfUrl }] =
    useLazyDownloadEmployeeExpenseQuery();

  const [
    expenseAccept,
    {
      isLoading: isExpenseAcceptLoading,
      isSuccess: isExpenseAcceptSuccess,
      isError: isExpenseAcceptError,
      error: expenseAcceptError,
    },
  ] = useExpenseAcceptMutation();

  const { currentRef } = usePrint("expense_details.pdf");

  useError(isError, error, "Get expense employee data fail");
  useError(isRejectError, rejectError, "Expanse reject fail");
  useError(isUpdateError, updateError, "Expanse update fail");
  useError(isExpenseAcceptError, expenseAcceptError, "Expense approved fail");

  const employeeInfo = data?.data?.employeeInfo;
  const employeeList = data?.data?.expenses?.map((el) => ({
    ...el,
    amount: el?.amount ? parseFloat(el?.amount?.toString()) : 0,
  }));
  const handleDownloadAll = (attachments: string[]) => {
    if (!attachments?.length) return;

    attachments.forEach((url) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = url.split("/").pop() || "file";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };
  useEffect(() => {
    if (isExpenseAcceptSuccess) {
      toast("success", "Expense approved successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpenseAcceptSuccess]);

  const columns: TableProps<TableType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Visit",
      dataIndex: "visit_id",
      render: (val) =>
        val ? (
          <button
            onClick={() =>
              handleOpen({ type: "VIEW_VISIT_DETAILS", data: val })
            }
            className="text-accent"
          >
            Visit Details
          </button>
        ) : (
          "n/a"
        ),
    },
    {
      title: "Expense Type",
      dataIndex: "type",
      sorter: (a, b) => a?.type?.localeCompare(b?.type),
      sortIcon: () => <SortIcon />,
      render: (val) => (val ? capitalize(val) : "n/a"),
    },

    {
      title: "Amount (BDT)",
      render: (val: TableType) =>
        editAmount?.id === val?.id ? (
          <form onSubmit={(e) => handleSubmitForUpdate(e, val?.id)}>
            <InputNumber
              ref={inputRef}
              value={editAmount?.amount ?? undefined}
              onChange={(val) => {
                return setEditAmount(
                  (prev) =>
                    prev && {
                      ...prev,
                      amount: val ?? undefined,
                    }
                );
              }}
            />
          </form>
        ) : (
          val?.amount ?? "n/a"
        ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (val) => val || "n/a",
    },
    {
      title: "Attachments",
      dataIndex: "expenseAttachment",
      render: (attachments) => {
        return attachments?.length ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                handleOpen({ type: "VIEW_ATTACHMENT", data: attachments })
              }
              className="text-accent"
            >
              View {attachments?.length > 1 ? `(${attachments?.length})` : ""}
            </button>
            <button
              onClick={() =>
                handleDownloadAll(attachments?.map((a: any) => a?.attachment))
              }
            >
              <DownloadIcon width={16} height={16} />
            </button>
          </div>
        ) : (
          "n/a"
        );
      },
    },
    {
      title: "Status",
      render: (val) => {
        const currentStatusLog = val?.status_change_log?.find(
          (s: any) => s?.new_status === val?.status
        );
        const STATUS_CONFIG = {
          pending: "status-warning",
          rejected: "status-danger",
          accepted: "status-success",
        };

        return (
          <div className="flex items-center gap-1">
            <span
              className={
                STATUS_CONFIG?.[val?.status as keyof typeof STATUS_CONFIG]
              }
            >
              {capitalize(val?.status)}
            </span>
            {currentStatusLog ? (
              <Popover
                content={
                  <span className="text-white">
                    {currentStatusLog?.created_by_name
                      ?.split("-")
                      ?.slice(1)
                      ?.join(" ")}
                  </span>
                }
                trigger="hover"
                overlayClassName="dark-popup-tooltip"
              >
                <Button
                  className="action-button border-0"
                  size="small"
                  icon={
                    <span>
                      <InfoIcon />
                    </span>
                  }
                />
              </Popover>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Action",
      render: (val: TableType) => {
        // console.log(val);
        const buttonClass =
          "w-7 h-7 bg-white rounded-full border justify-center items-center inline-flex cursor-pointer select-none";

        if (val?.status === "rejected" || val?.status === "accepted") {
          return null;
        }

        return (
          <Space align="center" size={10}>
            {editAmount?.id === val?.id ? (
              <>
                <Tooltip placement="top" title="Update">
                  <span
                    className={`${buttonClass} border-[#8fe6b7] text-complete`}
                    onClick={() => {
                      if (editAmount?.amount && editAmount?.amount < 0) {
                        toast("error", "Amount cannot be negative");
                        return;
                      }

                      if (!Number.isInteger(editAmount?.amount)) {
                        toast("error", "Amount must be integer");
                        return;
                      }

                      handleOpen({ type: "UPDATE", data: val?.id });
                    }}
                  >
                    <DoubleCheckIcon />
                  </span>
                </Tooltip>
                <Tooltip placement="top" title="Close">
                  <span
                    className={`${buttonClass} border-[#ffc5c1] text-dark-highlight`}
                    onClick={() => setEditAmount(undefined)}
                  >
                    <CloseIcon />
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip placement="top" title="Edit">
                  <span
                    className={`${buttonClass} border-[#b4c4ff] text-accent`}
                    onClick={() => {
                      setEditAmount({
                        id: val?.id,
                        amount: val?.amount ?? undefined,
                      });
                      setTimeout(() => {
                        inputRef.current?.focus();
                      }, 0);
                    }}
                  >
                    <EditIcon />
                  </span>
                </Tooltip>

                {val.status !== "rejected" && (
                  <Tooltip placement="top" title="Reject">
                    <span
                      className={`${buttonClass} border-[#ffc5c1] text-dark-highlight`}
                      onClick={() =>
                        handleOpen({ type: "REJECT", data: val?.id })
                      }
                    >
                      <CloseIcon />
                    </span>
                  </Tooltip>
                )}

                {val.status !== "accepted" && (
                  <Tooltip placement="top" title="Accept">
                    <span
                      className={`${buttonClass} border-[#8fe6b7] text-complete`}
                      onClick={() =>
                        handleOpen({ type: "APPROVE", data: val?.id })
                      }
                    >
                      <DoubleCheckIcon />
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const handleSubmitForUpdate = (e: FormEvent, id: number) => {
    e.preventDefault();
    handleOpen({ type: "UPDATE", data: id });
  };

  const handleExpenseUpdate = (expenseId: number) => {
    expenseUpdate({ id: expenseId, body: { amount: editAmount?.amount ?? 0 } });
  };

  const handleExpenseDelete = (expenseId: number) => {
    rejectExpense(expenseId);
  };

  // success toast message
  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Expense Successfully Updated!");
      setEditAmount(undefined);
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess]);

  // success toast message
  useEffect(() => {
    if (isRejectSuccess) {
      toast("success", "Expense Successfully Rejected!");
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRejectSuccess]);

  useEffect(() => {
    if (isExpenseAcceptSuccess) {
      toast("success", "Expense Successfully Approved!");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpenseAcceptSuccess]);

  // Pdf Download functionality
  const handleDownloadExpenceDetails = () => {
    downloadEmployeeExpense({
      params: {
        business_member_id: Number(viewId),
        ...(month ? { month: dayjs(month).format("MM-YYYY") } : {}),
      },
    });
  };

  useEffect(() => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "employee_expense.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [pdfUrl]);

  useEffect(() => {
    if (search.get("date")) {
      setMonth(dayjs(search.get("date"), "YYYY-MM"));
    }
  }, [search]);

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout title="Expense Details">
      {open.type === "REJECT" && (
        <ConfirmModal
          open={open?.open}
          handleOk={() => open?.data && handleExpenseDelete(open?.data)}
          handleClose={handleClose}
          okText="Reject"
          confirmTitle={`Reject Expense`}
          questionText={`Are you sure to reject this expense?`}
          okLoading={isRejectLoading}
        />
      )}
      {open.type === "VIEW_VISIT_DETAILS" && (
        <VisitDetailsModal
          open={open?.open}
          handleClose={handleClose}
          data={open?.data}
        />
      )}

      {open.type === "APPROVE" && (
        <ConfirmModal
          open={open?.open}
          handleOk={() => open?.data && expenseAccept(open.data)}
          handleClose={handleClose}
          okText="Approve"
          confirmTitle={`Approve Expense`}
          questionText={`Are you sure to approve this expense?`}
          okLoading={isExpenseAcceptLoading}
        />
      )}

      {open.type === "UPDATE" && (
        <ConfirmModal
          open={open?.open}
          handleOk={() => open?.data && handleExpenseUpdate(open?.data)}
          handleClose={handleClose}
          okText="Update"
          confirmTitle={`Update Expense`}
          questionText={`Are you sure to update this expense?`}
          okLoading={isUpdateLoading}
        />
      )}
      {open.type === "VIEW_ATTACHMENT" && (
        <CustomModal
          open={open.open}
          handleClose={handleClose}
          title="Attachments"
          footer={null}
        >
          <div className="grid grid-cols-3 justify-center items-center gap-3">
            {(open?.data as unknown as { attachment: string }[])?.map(
              (item, idx) => {
                return (
                  <div
                    className="h-full w-full relative group rounded-md overflow-hidden"
                    key={idx}
                  >
                    <Image
                      rootClassName="!h-full"
                      className="!object-cover rounded-md !w-full !h-full"
                      src={item?.attachment}
                    />
                    <a
                      href={item?.attachment}
                      target="_blank"
                      download
                      rel="noreferrer"
                      className="text-white hover:!text-white rounded-md absolute right-2 bottom-2 size-8 bg-black/40 flex items-center justify-center opacity-0 transition-all group-hover:opacity-100"
                    >
                      <DownloadIcon width={16} height={16} />
                    </a>
                  </div>
                );
              }
            )}
          </div>
        </CustomModal>
      )}
      <TopCard
        leftChildren={
          <Link to={backLink(location.pathname, 2)}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        <DatePicker
          format="MMMM YYYY"
          style={{ width: 160 }}
          suffixIcon={<DateIcon />}
          picker="month"
          onChange={(val) => {
            setMonth(val);
          }}
          value={month}
          className="clear-button-hide"
        />
        <Button
          className="middle-button normal-dark-button"
          loading={isDownloading}
          icon={<PdfIcon />}
          onClick={handleDownloadExpenceDetails}
        />
      </TopCard>

      <div className="p-6 bg-white rounded-md border border-extra-light">
        {isLoading ? (
          <ExpanseUserInfoSkeleton />
        ) : (
          <Space
            align="center"
            className="w-full mb-6 justify-between"
            size={24}
            wrap
          >
            <div className="flex flex-col gap-0.5">
              <h3 className="text-body text-lg font-semibold leading-normal">
                {employeeInfo?.name ?? "Name: n/a"}
              </h3>
              <p className="text-extra-gray text-sm font-medium leading-normal">
                ID: {employeeInfo?.employee_id ?? "n/a"}
              </p>
              <p className="text-display-body text-sm font-medium leading-normal">
                {employeeInfo?.designation ?? "Designation: n/a"}
              </p>
              <p className="text-display-body text-sm font-medium leading-normal">
                Department of
                <span className="text-tint-warning ml-1.5">
                  {employeeInfo?.department ?? "n/a"}
                </span>
              </p>
              <p className="text-display-body text-sm font-medium leading-normal">
                Email:
                <a
                  className="!text-accent hover:underline ml-1.5"
                  href={`mailto:${employeeInfo?.email}`}
                >
                  {employeeInfo?.email ?? "n/a"}
                </a>
              </p>
            </div>

            <Space wrap>
              <div className="p-6 bg-gray-light border border-light rounded-lg min-w-[236px]">
                <h6 className="text-[#001652] text-base font-medium leading-7">
                  Amount (BDT)
                </h6>
                <h4 className="text-[#001652] text-3xl font-bold  leading-[43px]">
                  {addCommaInNumber(data?.data?.totalAmount ?? 0)}
                </h4>
              </div>
              <div className="p-6 bg-status-success-bg border border-[#7DFEB9] rounded-lg min-w-[236px]">
                <h6 className="text-status-success-text text-base font-medium leading-7">
                  Approved Amount (BDT)
                </h6>
                <h4 className="text-status-success-text text-3xl font-bold  leading-[43px]">
                  {addCommaInNumber(data?.data?.approvedAmount ?? 0)}
                </h4>
              </div>
              <div className="p-6 bg-status-warning-bg border border-[#FFE176] rounded-lg min-w-[236px]">
                <h6 className="text-status-warning-text text-base font-medium leading-7">
                  Pending Amount (BDT)
                </h6>
                <h4 className="text-status-warning-text text-3xl font-bold  leading-[43px]">
                  {addCommaInNumber(data?.data?.pendingAmount ?? 0)}
                </h4>
              </div>
            </Space>
          </Space>
        )}

        <div>
          <CustomTable
            loading={isLoading}
            columns={columns}
            dataSource={addKeyInArray(employeeList)}
            className="header-table-color"
          />
        </div>

        <PrintableContent ref={currentRef}>
          <ExpenseDetailsPDF
            employeeInfo={employeeInfo || {}}
            expenseList={employeeList || []}
          />
        </PrintableContent>
      </div>
    </SidebarLayout>
  );
}
